// Word list loader utility for Wordle game
// Loads official Wordle word lists from public folder

let validGuesses = null;
let answerWords = null;
let loadPromise = null;

/**
 * Load word lists from JSON files
 * Uses singleton pattern to load only once
 * @returns {Promise<{validGuesses: Set, answerWords: Array}>}
 */
export async function loadWordLists() {
  // Return cached data if already loaded
  if (validGuesses && answerWords) {
    return { validGuesses, answerWords };
  }

  // Return existing promise if already loading
  if (loadPromise) {
    return loadPromise;
  }

  // Create new load promise
  loadPromise = (async () => {
    try {
      const [validGuessesRes, answerWordsRes] = await Promise.all([
        fetch('/wordlists/valid-guesses.json'),
        fetch('/wordlists/answer-words.json')
      ]);

      if (!validGuessesRes.ok || !answerWordsRes.ok) {
        throw new Error('Failed to load word lists');
      }

      const [validGuessesArray, answerWordsArray] = await Promise.all([
        validGuessesRes.json(),
        answerWordsRes.json()
      ]);

      // Store as Set for O(1) lookup performance
      validGuesses = new Set(validGuessesArray);
      answerWords = answerWordsArray;

      console.log(`Loaded ${validGuesses.size} valid guesses and ${answerWords.length} answer words`);

      return { validGuesses, answerWords };
    } catch (error) {
      console.error('Error loading word lists:', error);
      loadPromise = null;
      throw error;
    }
  })();

  return loadPromise;
}

/**
 * Check if a word is valid for Wordle
 * @param {string} word - The word to validate (case-insensitive)
 * @returns {boolean} - True if word is valid
 */
export function isValidWord(word) {
  if (!validGuesses) {
    console.warn('Word lists not loaded yet. Call loadWordLists() first.');
    return false;
  }
  return validGuesses.has(word.toUpperCase());
}

/**
 * Get all answer words (for daily word selection)
 * @returns {Array<string>} - Array of answer words
 */
export function getAnswerWords() {
  if (!answerWords) {
    console.warn('Word lists not loaded yet. Call loadWordLists() first.');
    return [];
  }
  return answerWords;
}

/**
 * Get a random answer word
 * @returns {string|null} - Random answer word or null if not loaded
 */
export function getRandomAnswerWord() {
  if (!answerWords || answerWords.length === 0) {
    console.warn('Word lists not loaded yet. Call loadWordLists() first.');
    return null;
  }
  return answerWords[Math.floor(Math.random() * answerWords.length)];
}

/**
 * Check if word lists are loaded
 * @returns {boolean}
 */
export function areWordListsLoaded() {
  return validGuesses !== null && answerWords !== null;
}
